import { useMutation } from '@apollo/client';
import { MarkIdentityValidationDocumentAsLoadedCommand } from '@contexts/IdentityValidation/domain';
import { AddDocumentCommand } from '@contexts/IdentityValidation/domain/commands/add-document.command';
import { DeleteDocumentCommand } from '@contexts/IdentityValidation/domain/commands/delete-document.command';
import {
  AddDocumentT,
  DeleteDocumentT,
  DocumentsCommandActions,
  MarkIdentityValidationDocumentAsLoadedT,
} from '@contexts/IdentityValidation/domain/ports/documents-command-actions.port';
import { AptunoApolloLink, ResponseApolloErrorMapping } from '@contexts/Shared';

import {
  AddDocumentCommandRequestDto,
  AddDocumentCommandResponseDto,
  DeleteDocumentCommandRequestDto,
  DeleteDocumentCommandResponseDto,
  MarkIdentityValidationDocumentAsLoadedRequestDto,
  MarkIdentityValidationDocumentAsLoadedResponseDto,
} from '../dtos/documents.dto';
import { markIdentityValidationDocumentAsLoaded } from '../schemas';
import { addDocument } from '../schemas/mutations/add-document.mutation';
import { deleteDocument } from '../schemas/mutations/delete-document.mutation';

export const GqlDocumentsCommandActions: DocumentsCommandActions = {
  useAddDocument: (): AddDocumentT => {
    const [action, { loading, data, error }] = useMutation<AddDocumentCommandResponseDto, AddDocumentCommandRequestDto>(
      addDocument,
      {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      }
    );

    return {
      loading,
      documentAdded: data?.addIdentityValidationDocument.value,
      error: error && ResponseApolloErrorMapping(error),
      addDocument: (command: AddDocumentCommand) =>
        action({
          variables: {
            identityValidationId: command.identityValidationId,
            documentToAdd: command.documentToAdd,
            fileExtension: command.fileExtension,
          },
        }),
    };
  },

  useDeleteDocument: (): DeleteDocumentT => {
    const [action, { loading, data, error }] = useMutation<
      DeleteDocumentCommandResponseDto,
      DeleteDocumentCommandRequestDto
    >(deleteDocument, {
      errorPolicy: 'all',
      context: { clientName: AptunoApolloLink.CREDIT_STUDY },
    });

    return {
      loading,
      documentDeleted: data?.deleteIdentityValidationDocument.value,
      error: error && ResponseApolloErrorMapping(error),
      deleteDocument: (command: DeleteDocumentCommand) =>
        action({
          variables: {
            identityValidationId: command.identityValidationId,
            documentToDelete: command.documentToDelete,
          },
        }),
    };
  },

  useMarkIdentityValidationDocumentAsLoaded: (): MarkIdentityValidationDocumentAsLoadedT => {
    const [action, { loading, data, error }] = useMutation<
      MarkIdentityValidationDocumentAsLoadedResponseDto,
      MarkIdentityValidationDocumentAsLoadedRequestDto
    >(markIdentityValidationDocumentAsLoaded, {
      errorPolicy: 'all',
      context: { clientName: AptunoApolloLink.CREDIT_STUDY },
    });

    return {
      loading,
      identityValidationDocumentMarkedAsLoaded: data?.markIdentityValidationDocumentAsLoaded.value,
      error: error && ResponseApolloErrorMapping(error),
      markIdentityValidationDocumentAsLoaded: (command: MarkIdentityValidationDocumentAsLoadedCommand) =>
        action({
          variables: {
            identityValidationId: command.identityValidationId.value,
            loadedDocument: command.documentName,
          },
        }),
    };
  },
};
